import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toApiError } from '../lib/api-error';
import { API_URL } from '../config';

export type InboxDomain = {
  id: string;
  domain: string;
  description: string | null;
  createdAt: Date;
};

export function useAllowedDomains() {
  return useQuery({
    queryKey: ['inbox-domains'],
    queryFn: async (): Promise<InboxDomain[]> => {
      const response = await fetch(`${API_URL}/email/domains`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to fetch allowed domains');
      }

      const data = await response.json();
      return data.domains;
    },
  });
}

export function useCreateDomain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { domain: string; description?: string }) => {
      const response = await fetch(`${API_URL}/email/domains`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to create domain');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inbox-domains'] });
    },
  });
}

export function useDeleteDomain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (domain: string) => {
      const response = await fetch(`${API_URL}/email/domains/${encodeURIComponent(domain)}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to delete domain');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inbox-domains'] });
    },
  });
}

// Org-scoped domain hooks (any authenticated user)

export function useOrgDomains() {
  return useQuery({
    queryKey: ['org-inbox-domains'],
    queryFn: async (): Promise<InboxDomain[]> => {
      const response = await fetch(`${API_URL}/email/org-domains`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to fetch org domains');
      }

      const data = await response.json();
      return data.domains;
    },
  });
}

export function useCreateOrgDomain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { domain: string; description?: string }) => {
      const response = await fetch(`${API_URL}/email/org-domains`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to add domain');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-inbox-domains'] });
    },
  });
}

export function useDeleteOrgDomain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (domainId: string) => {
      const response = await fetch(`${API_URL}/email/org-domains/${encodeURIComponent(domainId)}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to remove domain');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-inbox-domains'] });
    },
  });
}
